import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { IonContent, IonIcon, IonPage, useIonRouter, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  FrequentlyAskedQuestions,
  priceNormalize,
  ProfileInformation,
  SpecialistMatch,
  SpecialistStatistic,
  SupportedCurrency,
  Typography,
  TypographyVariantsEnum,
  AlertCard,
  ReviewListFeature,
} from '@mindly/ui-components';

import { setIsActive } from '../../redux/tabBar/reducer';
import { CancelOrRescheduleSection, PaymentPageContainer, PaymentPageRow } from './PaymentPage.style';
import {
  psychologistSelectorById,
  specialistsSelector,
  specialistSelectorPositionById,
} from '../../redux/specialists/selector';
import { SupportedCurrencies } from '../../redux/specialists/types';

import { getFirestoreGlobalVariablesThunk } from '../../redux/firestoreGlobalVariables/action';
import { euroRate, globalVariables } from '../../redux/firestoreGlobalVariables/selector';
import { DateTime } from 'luxon';
import { StripeMetadataObjectType, StripeResponseType, StripeSupportedCurrency } from './type';
import { userSelector } from '../../redux/user/selector';
import { correctPriceType, delay, getStripeInfo, makeid } from './utils';
import CardFormComponent from './CardFormComponent';
import { loadSavedCreditCards } from '../../redux/savedCreditCards/action';
import { IPromotionalCode, SystemMessagesType, UserType } from '../types';
import PaymentSkeleton from './PaymentSkeleton';
import { PrivateRoutes, PublicRoutes } from 'src/routes/navigation';
import { SecureInfo } from './SecureInfo/SecureInfo';
import { getConsultationByID } from '../../api/consultation';
import { addNew } from '../../redux/consultations/reducer';
import { addNewUserPsychologist } from '../../api/user';
import { updateUserPsychologist } from '../../redux/user/reducer';
import LoadPaymentModal from '../../modals/LoadPaymentModal/LoadPaymentModal';
import { getChatRoomsListBySpecialistId } from '../../api/agora/chatRooms';
import { ChatRoomsData } from '../../redux/chatRooms/types';
import useAgoraRtm from '../../context/AgoraContext/AgoraContext';
import { FormattedMessage, useIntl } from 'react-intl';
import useLangs from '../../context/LangsContext';
import { formatSystemMessage } from '../../context/AgoraContext/formatSystemMessage';
import { consultationTimeToFormatSeconds } from '../SchedulePage/utils';
import EmailSystem from '../../api/emails';
import { checkmarkOutline } from 'ionicons/icons';
import { specialistReviewsSelector } from '../../redux/specialistReviews/selector';
import { fetchReviews } from '../../redux/specialistReviews/thunk';
import { Haptics, NotificationType } from '@capacitor/haptics';
import { SPECIALIST_MATCH, SPECIALIST_STATISTIC } from '../../intercom-articles';
import useToast from '../../context/ToastContext/ToastContext';
import PageHeader from '../../components/PageHeader/PageHeader';
import { fetchSpecialistsList } from '../../redux/specialists/thunk';
import { removePromoCode } from '../../redux/promoCodes/promoCodes.reducer';
import { CouponPreviewDetailsType, useAnalytics } from '../../shared';
import DiscountInputFeature from '../../features/DiscountInputFeature/DiscountInputFeature';
import { applyCouponToPaymentIntent, clearCouponFromPaymentIntent } from '../../api/coupons';
import { attachPromoCodeToPaymentIntent } from 'src/components/PaymentPromoCode/utils';
import { useTranslation } from 'react-i18next';
import { getUsersPsychologistsListThunk } from 'src/redux/usersPsychologists/action';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { environment } from 'src/enviroments';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';

interface PaymentPageProps
  extends RouteComponentProps<{
    id: string;
    date?: string;
    time?: string;
  }> {}

const SESSION_DURATION = 50;
const stripe = loadStripe(environment.STRIPE_PUBLIC_KEY);

export const PaymentPage: FC<PaymentPageProps> = ({ match, history }) => {
  const dispatch = useDispatch();

  const { locale } = useLangs();
  const intl = useIntl();
  const { t } = useTranslation(['ui', 'gift-certificates']);

  const { logEvent, displayArticle } = useIntercomPlugin();
  const { showToast } = useToast();
  const { trackPredefinedEvent } = useAnalytics();

  const [isCouponExpanded, setIsCouponExpanded] = useState<boolean | null>(null);
  const [isPromotionalCodeExpanded, setIsPromotionalCodeExpanded] = useState<boolean | null>(null);

  const [isCouponLoading, setIsCouponLoading] = useState<boolean>(false);
  const [isDetachCouponLoading, setIsDetachCouponLoading] = useState<boolean>(false);
  const [isPromotionalCodeLoading, setIsPromotionalCodeLoading] = useState<boolean>(false);
  const [isEventTracked, setIsEventTracked] = useState(false);

  const { id: specialistID, date, time } = match.params;

  const { sendSystemMessage } = useAgoraRtm();

  const modalRef = useRef<HTMLIonModalElement | null>(null);

  const [retryNumber, setRetryNumber] = useState<number>(0);

  const [isLoading, setIsLoading] = useState(true);

  const [isPageView, setIsPageView] = useState(false);
  const [reloadScreen, setReloadScreen] = useState(true);

  const [promoCodeData, setPromoCodeData] = useState<IPromotionalCode | null>(null);

  const [couponPreviewDetails, setCouponPreviewDetails] = useState<CouponPreviewDetailsType | null>(null);
  const [couponPreviewFailureMessage, setCouponPreviewFailureMessage] = useState('');

  const [promoCodeFailureMessage, setPromoCodeFailureMessage] = useState('');

  const [info, setInfo] = useState<StripeResponseType>({
    paymentIntentID: '',
    secret: '',
    publishableKey: '',
    priceUAH: 0,
    priceInCurrency: 0,
    priceType: SupportedCurrencies.EUR,
    stripeChargeAmount: 0,
    stripeChargeCurrency: SupportedCurrencies.EUR,
  });
  const [paymentStatus, setPaymentStatus] = useState<null | 'confirmed' | 'paid' | 'unknown'>(null);
  const [isFetching, setIsFetching] = useState<null | 'start' | 'success'>(null);
  const [isTrial] = useState(true);
  const specialistList = useSelector(specialistsSelector);
  const globalVars = useSelector(globalVariables);
  const specialist = useSelector(psychologistSelectorById(specialistID));
  const currentUser = useSelector(userSelector);
  const reviews = useSelector(specialistReviewsSelector(specialistID));
  const specialistPosition = useSelector(specialistSelectorPositionById(specialistID));

  const euro = useSelector(euroRate);
  const navigation = useIonRouter();

  const FAQArticleID = useMemo(() => 7170292, []);

  const currentDateTime = useMemo(() => DateTime.fromFormat(`${date} ${time}`, 'yyyy-LL-dd T'), [date, time]);

  const amount: number = useMemo(() => {
    const promocodeAmount =
      typeof promoCodeData?.paymentIntent?.amount === 'number' ? promoCodeData?.paymentIntent?.amount / 100 : null;

    const couponAmount =
      typeof couponPreviewDetails?.payment_amount_final === 'number'
        ? couponPreviewDetails?.payment_amount_final / 100
        : null;

    return Number(couponAmount ?? promocodeAmount ?? info.stripeChargeAmount / 100);
  }, [promoCodeData?.price_in_currency, info.priceUAH, euro, couponPreviewDetails?.payment_amount_final]);

  const amountUAH: number = useMemo(() => {
    const promocodeAmount = promoCodeData?.price_in_currency ? +promoCodeData?.price_in_currency : null;

    const couponAmount =
      typeof couponPreviewDetails?.payment_amount_local === 'number'
        ? couponPreviewDetails?.payment_amount_local / 100
        : null;

    return couponAmount ?? promocodeAmount ?? info.priceUAH ?? 0;
  }, [
    promoCodeData?.price_in_currency,
    info.priceUAH,
    euro,
    couponPreviewDetails?.payment_amount_local,
    couponPreviewDetails,
  ]);

  const consultationID = useMemo(() => makeid(16), [specialist?.id, date, time]);

  const stripeMetadataObject: StripeMetadataObjectType | null = useMemo(() => {
    if (!currentUser?.id || !specialistID) return null;

    return {
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userID: currentUser?.id,
      consultationID,
      psychologistID: specialistID,
      consultationTime: DateTime.fromFormat(`${date} ${time}`, 'yyyy-LL-dd H:mm').toUTC().toISO(),
      videochatRoomID: makeid(20),
      amount: amountUAH,
    };
  }, [specialistID, date, time, currentUser?.id, amountUAH, consultationID]);

  const createNewStripeCustomer = useCallback(async () => {
    if (!stripeMetadataObject) return history.go(-1);

    try {
      const { data } = await getStripeInfo(stripeMetadataObject);
      if (data?.promotionalCode) {
        setPromoCodeData(data?.promotionalCode);
      }

      if (data?.couponResponse) {
        setCouponPreviewDetails(data?.couponResponse);
        setIsCouponExpanded(true);
      }

      trackPredefinedEvent('InitiateCheckout', {
        contents: [{ content_id: specialistID, content_type: 'product' }],
        value: data.stripeChargeAmount / 100,
        currency: correctPriceType(data.stripeChargeCurrency as SupportedCurrencies),
        eventInstanceId: consultationID,
        userId: currentUser?.id,
        ...(currentUser?.email && { email: currentUser?.email }),
        ...(currentUser?.display_name && { firstName: currentUser?.display_name }),
        ...(currentUser?.last_name && { lastName: currentUser?.last_name }),
      });
      setInfo(data);
      setIsLoading(false);
    } catch (error: any) {
      showToast({ error });
      history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
    }
  }, [stripeMetadataObject?.consultationTime, stripeMetadataObject?.psychologistID, history, specialistID]);

  useEffect(() => {
    if (specialist?.id && currentUser?.id && stripeMetadataObject?.consultationTime) {
      if (DateTime.local().toSeconds() > DateTime.fromFormat(`${date} ${time}`, 'yyyy-LL-dd H:mm').toSeconds()) {
        showToast({
          text: intl.formatMessage({
            id: 'bocking.error.notAllowed',
            defaultMessage: 'Цей час вже недоступний, оберіть будь ласка інший час.',
          }),
        });
        setIsLoading(false);
        history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
      }
    }
  }, [stripeMetadataObject?.consultationTime]);

  useEffect(() => {
    (async () => {
      if (paymentStatus !== 'confirmed' || retryNumber > 10) return;

      await delay(500);

      const consultation = await getConsultationByID(consultationID);
      if (Object.keys(consultation).length) {
        dispatch(
          addNew({
            ...consultation,
            // need to add this field to the consultation object because api doesn't return it
            psychologist_avatar: specialist?.specialist_list_photo_link,
          })
        );
        // need to get the latest usersPsychologistList with actual data from the server
        await dispatch(getUsersPsychologistsListThunk());
        await addNewUserPsychologist(currentUser!.id, specialistID);
        dispatch(updateUserPsychologist(specialistID));
        const { data: roomData } = await getChatRoomsListBySpecialistId(
          consultation.user_id,
          consultation.psychologist_id
        );
        await sendSystemMessage({
          roomData: roomData?.[0] as ChatRoomsData,
          specialistId: consultation.psychologist_id,
          user: currentUser as Omit<UserType, 'id'> & { id: string },
          message: formatSystemMessage(
            SystemMessagesType.session_booked,
            {
              display_name: currentUser?.display_name as string,
              consultation_time: consultation.consultation_time,
            },
            intl
          ),
          notifyMessage: `${intl.formatMessage({
            id: 'booking.notify_message.booked',
            defaultMessage: 'Заброньовав(ла) сеанс на',
          })} ${DateTime.fromSeconds(consultationTimeToFormatSeconds(consultation.consultation_time))
            .setZone(consultation.psychologist_timezone)
            .setLocale(locale)
            .toFormat('dd MMMM HH:mm')}`,
          locale,
        });
        if (promoCodeData) {
          dispatch(removePromoCode(promoCodeData?.code));
        }
        EmailSystem.sendSessionBooked(consultation.id);

        if (consultation.charge_type !== 'balance') {
          trackPredefinedEvent('Purchase', {
            contents: [{ content_id: specialistID, content_type: 'product' }],
            eventInstanceId: consultationID,
            value: info?.stripeChargeAmount / 100,
            currency: correctPriceType(info.priceType) as unknown as SupportedCurrency,
            userId: currentUser?.id,
            ...(currentUser?.email && { email: currentUser?.email }),
            ...(currentUser?.display_name && { firstName: currentUser?.display_name }),
            ...(currentUser?.last_name && { lastName: currentUser?.last_name }),
          });
        }

        setPaymentStatus('paid');
        Haptics.notification({ type: NotificationType.Success });
        setIsFetching('success');
      }

      if (retryNumber >= 10) {
        setPaymentStatus('unknown');
        setIsFetching('success');
      }

      setRetryNumber(retryNumber + 1);
    })();
  }, [paymentStatus, retryNumber, stripeMetadataObject, promoCodeData, specialist?.specialist_list_photo_link]);

  useIonViewWillEnter(() => {
    if (reloadScreen) logEvent('Enter first session payment page');
  }, []);

  useEffect(() => {
    if (!isPageView) return;
    (async () => {
      try {
        await createNewStripeCustomer();
      } catch (error) {
        showToast({ error });
      }
    })();
  }, [stripeMetadataObject?.consultationTime, date, time, isPageView, reloadScreen]);

  useIonViewWillEnter(async () => {
    if (!currentUser?.id) return history.push(PublicRoutes.AUTH);
    if (!reloadScreen) {
      setIsLoading(false);
      return setReloadScreen(true);
    }
    try {
      setIsEventTracked(false);
      setIsPageView(true);
      setIsLoading(true);

      dispatch(setIsActive(false));
      await Promise.all([
        await dispatch(loadSavedCreditCards(currentUser?.id)),
        dispatch(fetchReviews({ specialistID })),
        !specialistList?.length && (await dispatch(fetchSpecialistsList(locale))),
        await dispatch(getFirestoreGlobalVariablesThunk()),
      ]);
    } catch (error) {
      showToast({ error });
    }
  }, [specialistID, currentUser?.id, date, time, locale, reloadScreen]);

  useIonViewWillLeave(() => {
    if (!reloadScreen) return;
    setInfo({
      paymentIntentID: '',
      secret: '',
      publishableKey: '',
      priceUAH: 0,
      priceInCurrency: 0,
      priceType: SupportedCurrencies.EUR,
      stripeChargeAmount: 0,
      stripeChargeCurrency: SupportedCurrencies.EUR,
    });
    setIsPageView(false);
    setPromoCodeData(null);
    setPaymentStatus(null);
    setIsFetching(null);
    setCouponPreviewDetails(null);
    setCouponPreviewFailureMessage('');
    setIsCouponExpanded(false);
    setIsCouponLoading(false);
    setRetryNumber(0);
    setPromoCodeFailureMessage('');
    setIsLoading(false);
    setIsPromotionalCodeExpanded(false);
    setIsPromotionalCodeLoading(false);
    if (!navigation.routeInfo.pathname.startsWith(PrivateRoutes.PSYCHOLOGIST_PROFILE_TAB.replace('/:id', ''))) {
      dispatch(setIsActive(true));
    }
  }, [reloadScreen, navigation.routeInfo.pathname]);

  useEffect(() => {
    if (!isPageView) return;
    if (!specialist) return;
    if (isEventTracked) return;
    if (!currentUser) return;
    if (isLoading) return;

    setIsEventTracked(true);
    trackPredefinedEvent('TRIAL_CHECKOUT_SCREEN_VIEWED', {
      specialist_id: specialist.id,
      specialist_name: `${specialist.first_name} ${specialist.last_name}`,
      date_chosen: DateTime.fromFormat(`${date} ${time}`, 'yyyy-LL-dd T').toUTC().toISO(),
      specialist_order_position: specialistPosition,
    });
  }, [
    specialist,
    isPageView,
    isEventTracked,
    date,
    time,
    currentUser,
    info.stripeChargeAmount,
    info.stripeChargeCurrency,
    isLoading,
    specialistPosition,
  ]);

  const onCouponSubmitHandler = useCallback(
    async (coupon: string) => {
      try {
        setIsCouponLoading(true);
        const response = await applyCouponToPaymentIntent(info.paymentIntentID, coupon.trim());
        setCouponPreviewDetails(response);
      } catch (error: any) {
        const errors = error.error.map((el: any) => el.msg);
        setCouponPreviewFailureMessage(errors?.length > 1 ? errors.join(', ') : errors[0]);
      } finally {
        setIsCouponLoading(false);
      }
    },
    [info.paymentIntentID, couponPreviewDetails]
  );

  const onCouponCancelHandler = useCallback(async () => {
    try {
      if (!couponPreviewDetails?.coupon_code || !info.paymentIntentID) {
        return;
      }
      setIsDetachCouponLoading(true);

      await clearCouponFromPaymentIntent(info.paymentIntentID, couponPreviewDetails?.coupon_code);

      setCouponPreviewDetails(null);
      setIsCouponExpanded(false);
      setIsCouponLoading(false);
      setIsPromotionalCodeExpanded(false);
      setCouponPreviewFailureMessage('');
    } catch (error) {
      showToast({ error });
    } finally {
      setIsDetachCouponLoading(false);
    }
  }, [info.paymentIntentID, couponPreviewDetails?.coupon_code]);

  const onPromotionalCodeSubmitHandler = useCallback(
    async (promotionalCode: string) => {
      setIsPromotionalCodeLoading(true);

      const [success, failure] = await attachPromoCodeToPaymentIntent(
        info.paymentIntentID,
        promotionalCode.trim(),
        intl
      );

      if (success) {
        setPromoCodeFailureMessage('');
        setPromoCodeData(success);
      }
      if (failure) {
        setPromoCodeFailureMessage(failure);
      }

      setIsPromotionalCodeLoading(false);
    },
    [info.paymentIntentID]
  );

  const handleLoadMoreReviews = useCallback(() => {
    dispatch(fetchReviews({ specialistID }));
  }, [specialistID]);

  return (
    <IonPage>
      <PageHeader
        backChevron
        intercomId={FAQArticleID}
        title={intl.formatMessage({
          id: 'bocking.payment.title',
          defaultMessage: 'Оплата',
        })}
        subtitle={intl.formatMessage(
          {
            id: 'bocking.step',
            defaultMessage: 'Крок {from} із {to}',
          },
          { from: 2, to: 2 }
        )}
        loading={isLoading}
        showPromoCodeTimer
      />
      <IonContent>
        {isLoading ? (
          <Container>
            <PaymentSkeleton />
          </Container>
        ) : (
          <>
            <LoadPaymentModal
              history={history}
              isTrial={isTrial}
              ref={modalRef}
              fetchingStatus={isFetching}
              paymentStatus={paymentStatus}
              loadType={'payment'}
            />
            <PaymentPageContainer>
              <PaymentPageRow className={'pb-20'}>
                <ProfileInformation
                  isLoading={isLoading}
                  specialistAvatar={specialist?.avatar_link as string}
                  specialistFirstName={specialist?.first_name}
                  specialistLastName={specialist?.last_name}
                  specialistSpecialization={specialist?.specialization as string}
                  countryCode={specialist?.country_of_birthday as string}
                  countOfReviews={
                    (specialist?.reviews_count ?? 0) >= globalVars.specialistReviewsCountForShowing
                      ? specialist?.reviews_count
                      : undefined
                  }
                  averageStarsCount={
                    (specialist?.reviews_count ?? 0) >= globalVars.specialistReviewsCountForShowing
                      ? specialist?.reviews_average_rating
                      : undefined
                  }
                  translations={{
                    reviews: [
                      intl.formatMessage({
                        id: 'other.specialistReviews.one',
                        defaultMessage: 'відгук',
                      }),
                      intl.formatMessage({
                        id: 'other.specialistReviews.many',
                        defaultMessage: 'відгуки',
                      }),
                      intl.formatMessage({
                        id: 'other.specialistReviews.several',
                        defaultMessage: 'відгуків',
                      }),
                    ],
                  }}
                />
              </PaymentPageRow>

              <div className={'flex flex-col pb-5 px-4'}>
                <Typography variant={TypographyVariantsEnum.Text18Semi} className={'capitalize'}>
                  {currentDateTime.setLocale(locale).toFormat('EEEE')},{' '}
                  {currentDateTime.setLocale(locale).toFormat('d MMMM')},{' '}
                  {currentDateTime.toLocaleString(DateTime.TIME_SIMPLE)} -{' '}
                  {currentDateTime.plus({ minute: SESSION_DURATION }).toLocaleString(DateTime.TIME_SIMPLE)}
                </Typography>
                <Typography variant={TypographyVariantsEnum.Text14Regular}>
                  {currentDateTime.toFormat('z')} ({currentDateTime.toFormat('ZZZZ')})
                </Typography>
              </div>

              {!promoCodeData && (
                <PaymentPageRow withContainer className={'!pb-5'}>
                  <DiscountInputFeature
                    onCancel={onCouponCancelHandler}
                    onSubmit={onCouponSubmitHandler}
                    setIsExpanded={setIsCouponExpanded}
                    isExpanded={isCouponExpanded}
                    isLoading={isCouponLoading}
                    showLoader={isDetachCouponLoading}
                    failureMessage={couponPreviewFailureMessage}
                    couponPreviewDetails={couponPreviewDetails}
                    type={'coupon'}
                  />
                </PaymentPageRow>
              )}

              {!couponPreviewDetails?.coupon_code && (
                <PaymentPageRow withContainer className={'!pb-5'}>
                  <DiscountInputFeature
                    onSubmit={onPromotionalCodeSubmitHandler}
                    setIsExpanded={setIsPromotionalCodeExpanded}
                    isExpanded={isPromotionalCodeExpanded}
                    isLoading={isPromotionalCodeLoading}
                    failureMessage={promoCodeFailureMessage}
                    promoCodeData={promoCodeData}
                    type={'promo'}
                  />
                </PaymentPageRow>
              )}

              <div className={'px-4 pb-5'}>
                <div className={'flex items-center justify-between'}>
                  <Typography variant={TypographyVariantsEnum.Text18Semi}>
                    <FormattedMessage id={'booking.payment.pay'} defaultMessage={'До сплати'} />
                  </Typography>
                  <div className={'flex items-center gap-2'}>
                    {(promoCodeData?.price_default || couponPreviewDetails) && (
                      <Typography variant={TypographyVariantsEnum.Text18Regular} className={'line-through'}>
                        {priceNormalize({
                          price: info?.stripeChargeAmount / 100,
                          currency: correctPriceType(info.priceType) as unknown as SupportedCurrency,
                          extendedUsd: true,
                        })}
                      </Typography>
                    )}
                    <Typography variant={TypographyVariantsEnum.Text18Semi}>
                      {priceNormalize({
                        price: couponPreviewDetails?.payment_amount_final
                          ? couponPreviewDetails?.payment_amount_final / 100
                          : amount,
                        currency: correctPriceType(info.priceType) as unknown as SupportedCurrency,
                        extendedUsd: true,
                      })}
                    </Typography>
                  </div>
                </div>
                {!StripeSupportedCurrency.includes(info.priceType) && (
                  <Typography
                    variant={TypographyVariantsEnum.Text14Regular}
                    opacity={50}
                    className={'flex justify-end'}
                  >
                    <FormattedMessage id={'bocking.in.uan'} defaultMessage={'Приблизно'} />{' '}
                    {priceNormalize({
                      price: amountUAH,
                      currency: SupportedCurrency.UAH,
                    })}
                  </Typography>
                )}
              </div>

              {couponPreviewDetails?.payment_amount_final === 0 && couponPreviewDetails?.coupon_type !== 'balance' && (
                <div className={'px-4 pb-5'}>
                  <AlertCard
                    text={t('gift-certificates:checkout.gift_card.full_cover_notice')}
                    icon={'☝️'}
                    variant={'blue'}
                    size={'XY4'}
                  />
                </div>
              )}

              {(couponPreviewDetails?.payment_amount_charged ??
                promoCodeData?.paymentIntent?.amount ??
                info.stripeChargeAmount) > 0 && (
                <PaymentPageRow className={'pb-30'}>
                  <Elements
                    stripe={stripe}
                    options={{
                      mode: 'payment',
                      amount:
                        couponPreviewDetails?.payment_amount_charged ??
                        promoCodeData?.paymentIntent?.amount ??
                        info.stripeChargeAmount,
                      currency: info.stripeChargeCurrency.toLowerCase(),
                      setup_future_usage: 'on_session',
                    }}
                  >
                    {isPageView && (
                      <CardFormComponent
                        selectedPsychologist={specialist ?? undefined}
                        info={info}
                        promoCodeData={promoCodeData}
                        setIsFetching={setIsFetching}
                        setPaymentStatus={setPaymentStatus}
                        couponPreviewDetails={couponPreviewDetails}
                        disabled={isCouponLoading || isDetachCouponLoading || isPromotionalCodeLoading}
                        consultationTime={stripeMetadataObject?.consultationTime ?? ''}
                        consultationID={consultationID}
                      />
                    )}
                  </Elements>
                </PaymentPageRow>
              )}

              <PaymentPageRow withContainer border className={'pb-25'}>
                <CancelOrRescheduleSection>
                  <IonIcon icon={checkmarkOutline} />
                  <Typography variant={TypographyVariantsEnum.Text16Regular} className={'leading-5'}>
                    <FormattedMessage
                      id={'booking.payment.cancel_or_reschedule'}
                      defaultMessage={'Скасуйте або перенесіть сеанс за 24 години до початку'}
                    />
                  </Typography>
                </CancelOrRescheduleSection>
              </PaymentPageRow>
              <PaymentPageRow
                border={!!specialist?.percent_match}
                className={!!specialist?.percent_match ? 'pb-25 pt-25' : ''}
              >
                <SpecialistMatch
                  className={'specialist-match'}
                  learnMoreClick={() => {
                    displayArticle(SPECIALIST_MATCH);
                  }}
                  isLoading={isLoading}
                  percentMatch={specialist?.percent_match ?? undefined}
                  specialistFirstName={specialist?.first_name as string}
                  translations={{
                    matchYou: intl.formatMessage({
                      id: 'specialist.profile.matchYou',
                      defaultMessage: 'підходить Вам на',
                    }),
                    howWeMatchThis: intl.formatMessage({
                      id: 'specialist.profile.howWeMatchThis',
                      defaultMessage: 'Дізнатися як ми це прогнозуємо',
                    }),
                  }}
                />
              </PaymentPageRow>
              <PaymentPageRow
                border={!!specialist?.super_specialist || !!specialist?.session_count_by_3_days}
                className={!!specialist?.super_specialist || !!specialist?.session_count_by_3_days ? 'pb-25 pt-25' : ''}
              >
                <SpecialistStatistic
                  className={'specialist-stats'}
                  trendingUpIcon={'./trendingUpOutline.svg'}
                  superSpecialistIcon={'./flashOutline.svg'}
                  learnMoreClick={() => {
                    displayArticle(SPECIALIST_STATISTIC);
                  }}
                  specialistFirstName={specialist?.first_name ?? ''}
                  specialistLastName={specialist?.last_name ?? ''}
                  isSuperSpecialist={!!specialist?.super_specialist}
                  countConsultationFor3Days={specialist?.session_count_by_3_days ?? 0}
                  isLoading={isLoading}
                  translations={{
                    bookedSession: intl.formatMessage({
                      id: 'specialist.profile.bookedSession',
                      defaultMessage: 'заброньовано за останні 72 години',
                    }),
                    superSpecialistInfo: intl.formatMessage(
                      {
                        id: 'specialist.profile.superSpecialistInfo',
                        defaultMessage: `високорейтинговий і надійний спеціаліст.{space}`,
                      },
                      { space: ' ' }
                    ),
                    knowMore: intl.formatMessage({
                      id: 'specialist.profile.knowMore',
                      defaultMessage: `Дізнатися більше`,
                    }),
                    superSpecialist: intl.formatMessage({
                      id: 'specialist.profile.superSpecialist',
                      defaultMessage: 'Супер спеціаліст',
                    }),
                    popularSpecialist: intl.formatMessage({
                      id: 'specialist.profile.popularSpecialist',
                      defaultMessage: 'Популярний спеціаліст',
                    }),
                    sessions: [
                      intl.formatMessage({
                        id: 'other.sessions.one',
                        defaultMessage: 'сеанс',
                      }),
                      intl.formatMessage({
                        id: 'other.sessions.many',
                        defaultMessage: 'сеанси',
                      }),
                      intl.formatMessage({
                        id: 'other.sessions.several',
                        defaultMessage: 'сеансів',
                      }),
                    ],
                  }}
                />
              </PaymentPageRow>
              <PaymentPageRow className={'pb-15 !px-4'}>
                {specialist && Array.isArray(reviews) && reviews.length > 0 && (
                  <ReviewListFeature
                    list={reviews}
                    rating={specialist?.reviews_average_rating}
                    reviews={specialist?.reviews_count}
                    t={t}
                    className="mt-6"
                    limit={5}
                    onLoadMore={handleLoadMoreReviews}
                    specialistName={`${specialist?.first_name} ${specialist?.last_name.charAt(0)}.`}
                    specialistAvatar={specialist?.specialist_list_photo_link ?? ''}
                  />
                )}
              </PaymentPageRow>

              <PaymentPageRow
                className={'pb-15'}
                onClick={() => {
                  displayArticle(FAQArticleID);
                }}
              >
                <FrequentlyAskedQuestions
                  text={intl.formatMessage({
                    id: 'bocking.faq',
                    defaultMessage: 'Питання що часто задаються',
                  })}
                  alignment={'center'}
                />
              </PaymentPageRow>

              <SecureInfo order={'text-last'} />
            </PaymentPageContainer>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};
